import { useCallback, useEffect, useState } from "react"
import {
  type BetterDocUsageEvaluation,
  fetchBetterDocUsageForConsultation,
} from "@/modules/patients/detail/better-doc/lib/fetch-better-doc-usage-for-consultation"
import { BetterDoc, BetterDocConsultationDetail, Country, PointOfCareType } from "@/types/__generated__/union-types"

type UseBetterDocUsageForConsultationParameters = {
  country: Country | undefined
  patientId: string
  diagnosisCode: string | null
  effectivePointOfCare: PointOfCareType | null
  specialtyId: string | null
  enabled: boolean
}

export type BetterDocUsageForConsultationResult = {
  betterDocUsage: BetterDoc | null
  missingConsultationDetailsThatCanChangeBetterDocUsage: ReadonlyArray<BetterDocConsultationDetail>
  isBetterDocUsagePending: boolean
  shouldShowBetterDocUsageFailure: boolean
  dismissBetterDocUsageFailure: () => void
}

type BetterDocUsageEvaluationState =
  | { status: "PENDING" }
  | { status: "EVALUATED"; betterDocUsageEvaluation: BetterDocUsageEvaluation }
  | { status: "FAILED"; isFailureDismissed: boolean }

const PENDING_EVALUATION: BetterDocUsageEvaluationState = { status: "PENDING" }

const FAILED_EVALUATION: BetterDocUsageEvaluationState = { status: "FAILED", isFailureDismissed: false }

const DISMISSED_FAILED_EVALUATION: BetterDocUsageEvaluationState = { status: "FAILED", isFailureDismissed: true }

const NOT_EVALUATED: Pick<
  BetterDocUsageForConsultationResult,
  "betterDocUsage" | "missingConsultationDetailsThatCanChangeBetterDocUsage"
> = {
  betterDocUsage: null,
  missingConsultationDetailsThatCanChangeBetterDocUsage: [],
}

export const useBetterDocUsageForConsultation = ({
  country,
  patientId,
  diagnosisCode,
  effectivePointOfCare,
  specialtyId,
  enabled,
}: UseBetterDocUsageForConsultationParameters): BetterDocUsageForConsultationResult => {
  const [evaluation, setEvaluation] = useState<BetterDocUsageEvaluationState>(PENDING_EVALUATION)

  useEffect(() => {
    if (!enabled || !country || !patientId || !effectivePointOfCare) {
      return
    }

    let isLatestEvaluation = true

    fetchBetterDocUsageForConsultation(country, {
      patientId,
      diagnosisCode,
      effectivePointOfCare,
      specialtyId,
    })
      .then((betterDocUsageEvaluation) => {
        if (isLatestEvaluation) {
          setEvaluation(
            betterDocUsageEvaluation ? { status: "EVALUATED", betterDocUsageEvaluation } : FAILED_EVALUATION,
          )
        }
      })
      .catch(() => {
        if (isLatestEvaluation) {
          setEvaluation(FAILED_EVALUATION)
        }
      })

    return () => {
      isLatestEvaluation = false
    }
  }, [country, enabled, patientId, diagnosisCode, effectivePointOfCare, specialtyId])

  const dismissBetterDocUsageFailure = useCallback(() => {
    setEvaluation((currentEvaluation) =>
      currentEvaluation.status === "FAILED" ? DISMISSED_FAILED_EVALUATION : currentEvaluation,
    )
  }, [])

  const betterDocUsageEvaluation =
    enabled && evaluation.status === "EVALUATED" ? evaluation.betterDocUsageEvaluation : NOT_EVALUATED

  return {
    ...betterDocUsageEvaluation,
    isBetterDocUsagePending: enabled && evaluation.status === "PENDING",
    shouldShowBetterDocUsageFailure: enabled && evaluation.status === "FAILED" && !evaluation.isFailureDismissed,
    dismissBetterDocUsageFailure,
  }
}
